import { Component, Event, EventEmitter, Fragment, h, Prop, State } from '@stencil/core';
import { FileDoc, FileType, MarkupDoc } from 'common';
import { DocumentSelectorModal } from '../../components/concourse-documents/document-selector-modal';
import { PhotosSelectorModal } from '../../components/concourse-photos/photos-selector-modal';
import { DocumentViewerPreviewPage } from './submittal-files-viewer';
import { SharedUtils } from '../../global/utils/SharedUtils';

@Component({
  tag: 'submittal-preview-card',
  styleUrls: ['../../components/concourse-documents/document-viewer.css', '../../components/concourse-documents/document-viewer-pages.css', 'concourse-submittals.css'],
})
export class SubmittalPreviewCard {
  @Prop() pages: DocumentViewerPreviewPage[] = [];
  @Prop() markups: MarkupDoc[] = [];
  @Prop() selectedPageIndex: number = 0;
  @Prop() loading: boolean = false;
  @Prop() projectId: string;
  @Prop() canWrite: boolean = false;
  @Prop() fileRoles: string[] = [];
  @Prop() fileType: FileType = FileType.DOCUMENT;

  @Event() selectPage: EventEmitter<number>;
  @Event() fileAdded: EventEmitter<FileDoc>;

  @State() pendingUploads: File[] = [];

  private browseButton: HTMLInputElement;
  private feedPopoverRef: HTMLIonPopoverElement;

  async showAttachmentSelector() {
    const files = await DocumentSelectorModal.present(this.projectId, [FileType.DOCUMENT, FileType.SCRAPBOOK, FileType.DRAWING, FileType.SPECIFICATION]);
    if (files) {
      files.forEach(file => this.fileAdded.emit(file));
    }
    this.feedPopoverRef?.dismiss();
  }

  async showPhotoSelector() {
    const photos = await PhotosSelectorModal.present(this.projectId);
    if (photos) {
      photos.forEach(file => this.fileAdded.emit(file));
    }
    this.feedPopoverRef?.dismiss();
  }

  async addPendingFiles(files: File[]) {
    const transfer = new DataTransfer();
    files.forEach(f => transfer.items.add(f));
    this.pendingUploads = await SharedUtils.filterAndValidateUploads(transfer.files, this.pendingUploads);
  }

  onFileUploaded(file: FileDoc, source: File) {
    this.pendingUploads = this.pendingUploads.filter(f => f !== source);
    this.fileAdded.emit(file);
  }

  render() {
    const hasPages = this.pages?.length > 0;

    return (
      <div class="submittal-preview-card">
        <ion-card-header class="submittal-panel-card__header ion-hide-md-down">
          <ion-card-title class="typography-body1-500">Preview</ion-card-title>
        </ion-card-header>
        {this.pendingUploads.length > 0 && (
          <div class="submittal-preview-card__uploads cc-padding-horizontal">
            {this.pendingUploads.map(file =>
              file.type?.startsWith('image') ? (
                <photo-uploader-instance class="comment-photo-upload" projectId={this.projectId} upload={file} onUploaded={e => this.onFileUploaded(e.detail, file)} />
              ) : (
                <file-uploader-instance
                  layout="square"
                  projectId={this.projectId}
                  file={file}
                  fileType={this.fileType}
                  fileRoles={this.fileRoles}
                  onUploaded={e => this.onFileUploaded(e.detail, file)}
                  onCancel={() => (this.pendingUploads = this.pendingUploads.filter(f => f !== file))}
                />
              ),
            )}
          </div>
        )}
        <div class="submittal-preview-card__body">
          {hasPages && (
            <div class="document-viewer__right-sidebar submittal-preview-card__thumbs cc-scrollbars cc-padding-vertical">
              {this.pages.map((page, index) => this.renderThumb(page, index))}
            </div>
          )}
          {this.loading && (
            <div class={`submittal-preview-card__loading${hasPages ? ' submittal-preview-card__loading--inline' : ''}`}>
              <ion-spinner name="crescent" />
              <ion-label>Loading pages...</ion-label>
            </div>
          )}
        </div>
        <ion-popover ref={el => (this.feedPopoverRef = el as HTMLIonPopoverElement)} side="bottom" alignment="end">
          <ion-content>
            <ion-list class="ion-no-padding">
              <ion-item button lines="none" onClick={() => this.showAttachmentSelector()}>
                <ion-icon slot="start" color="text-color" name="document-outline"></ion-icon>
                Select File
              </ion-item>
              <ion-item
                button
                lines="none"
                onClick={() => {
                  this.browseButton.click();
                  this.feedPopoverRef?.dismiss();
                }}
              >
                <ion-icon slot="start" color="text-color" name="cloud-upload-outline"></ion-icon>
                Upload File
              </ion-item>
              <ion-item button lines="none" onClick={() => this.showPhotoSelector()}>
                <ion-icon slot="start" color="text-color" name="image-outline"></ion-icon>
                Select Photo
              </ion-item>
            </ion-list>
          </ion-content>
        </ion-popover>
        <input
          type="file"
          ref={el => (this.browseButton = el)}
          onChange={e => {
            void this.addPendingFiles(Array.from((e.target as HTMLInputElement).files));
            (e.target as HTMLInputElement).value = '';
          }}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.webp"
          hidden
        />
      </div>
    );
  }

  private renderThumb(page: DocumentViewerPreviewPage, index: number) {
    const isSelected = this.selectedPageIndex === index;
    const thumbWidth = page.thumb_width || 816;
    const thumbHeight = page.thumb_height || 1056;
    const width = 180;
    const height = Math.round((width / thumbWidth) * thumbHeight);

    const separator = page.showFileSeparator && page.separatorLabel && (
      <div class="document-separator">
        <span class="document-separator__label">{page.separatorLabel}</span>
      </div>
    );

    if (page.svg) {
      return (
        <Fragment>
          {separator}
          <div class={`thumb${isSelected ? ' thumb--selected' : ''}`} onClick={() => this.selectPage.emit(index)}>
            <div class="thumb__svg" style={{ width: `${width}px`, height: `${height}px` }} innerHTML={page.svg}></div>
            <small>{page.label}</small>
          </div>
        </Fragment>
      );
    }

    if (page.thumb) {
      return (
        <Fragment>
          {separator}
          <div class={`thumb${isSelected ? ' thumb--selected' : ''}`} onClick={() => this.selectPage.emit(index)}>
            <img src={`data:image/jpeg;base64,${page.thumb}`} width={width} height={height} alt={page.label} />
          </div>
        </Fragment>
      );
    }

    return (
      <Fragment>
        {separator}
        <div class={`thumb thumb--placeholder${isSelected ? ' thumb--selected' : ''}`} onClick={() => this.selectPage.emit(index)}>
          <div class="thumb__placeholder-label">{page.label}</div>
        </div>
      </Fragment>
    );
  }
}
